import { useState, useRef } from "react";
import { motion, AnimatePresence, useMotionValue, useSpring } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { MaskText, SectionLabel, Reveal } from "./primitives";

const SERVICES = [
  {
    n: "01",
    title: "Brand Identity",
    desc: "Naming, logo systems, visual language and guidelines that make you unmistakable.",
    img: "https://images.unsplash.com/photo-1517842645767-c639042777db?q=80&w=1200&auto=format&fit=crop",
  },
  {
    n: "02",
    title: "Marketing Strategy",
    desc: "Positioning, campaigns and go-to-market plans grounded in data and craft.",
    img: "https://images.unsplash.com/photo-1460925895917-afdab827c52f?q=80&w=1200&auto=format&fit=crop",
  },
  {
    n: "03",
    title: "Social & Content",
    desc: "Always-on content, community and paid social that compounds attention.",
    img: "https://images.unsplash.com/photo-1611162617474-5b21e879e113?q=80&w=1200&auto=format&fit=crop",
  },
  {
    n: "04",
    title: "Web & Digital",
    desc: "High-craft websites and product experiences engineered to convert.",
    img: "https://images.unsplash.com/photo-1547658719-da2b51169166?q=80&w=1200&auto=format&fit=crop",
  },
  {
    n: "05",
    title: "Film & Motion",
    desc: "Commercials, motion design and brand films shot across the region.",
    img: "https://images.unsplash.com/photo-1485846234645-a62644f84728?q=80&w=1200&auto=format&fit=crop",
  },
];

export default function Services() {
  const [active, setActive] = useState(null);
  const containerRef = useRef(null);
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const x = useSpring(mx, { stiffness: 150, damping: 20 });
  const y = useSpring(my, { stiffness: 150, damping: 20 });

  const onMove = (e) => {
    const rect = containerRef.current.getBoundingClientRect();
    mx.set(e.clientX - rect.left - 160);
    my.set(e.clientY - rect.top - 100);
  };

  return (
    <section
      id="services"
      data-testid="services-section"
      className="relative bg-dm-light py-24 md:py-32"
    >
      <div className="mx-auto max-w-[1500px] px-6 md:px-12">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-14 md:mb-20">
          <div>
            <SectionLabel tone="maroon" className="text-dm-maroon">What we do</SectionLabel>
            <MaskText
              lines={["Capabilities that", "cover every angle."]}
              className="mt-5 font-display font-extrabold text-dm-dark tracking-tighter leading-[0.95] text-4xl sm:text-5xl md:text-6xl"
            />
          </div>
          <Reveal delay={0.1}>
            <p className="max-w-sm text-dm-dark/60 leading-relaxed">
              One team, end-to-end. From the first strategy workshop to the final
              frame delivered — we own the outcome.
            </p>
          </Reveal>
        </div>

        <div
          ref={containerRef}
          onMouseMove={onMove}
          className="relative border-t"
          style={{ borderColor: "rgba(10,10,10,0.12)" }}
        >
          {/* floating preview */}
          <AnimatePresence>
            {active !== null && (
              <motion.div
                className="pointer-events-none absolute z-20 hidden lg:block w-[320px] h-[200px] overflow-hidden clip-tl shadow-2xl"
                style={{ x, y, top: 0, left: 0 }}
                initial={{ opacity: 0, scale: 0.85 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.85 }}
                transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
              >
                <img src={SERVICES[active].img} alt="" className="h-full w-full object-cover" />
                <div className="absolute inset-0" style={{ background: "rgba(39,90,113,0.2)" }} />
              </motion.div>
            )}
          </AnimatePresence>

          {SERVICES.map((s, i) => (
            <Reveal key={s.n} delay={i * 0.05}>
              <div
                data-testid={`service-row-${i}`}
                onMouseEnter={() => setActive(i)}
                onMouseLeave={() => setActive(null)}
                className="group relative grid grid-cols-12 items-center gap-4 border-b py-7 md:py-9 cursor-pointer transition-colors duration-300"
                style={{ borderColor: "rgba(10,10,10,0.12)" }}
              >
                <span className="col-span-2 md:col-span-1 font-oswald text-sm text-dm-teal">{s.n}</span>
                <h3
                  className="col-span-10 md:col-span-4 font-display font-bold text-3xl md:text-5xl tracking-tight text-dm-dark transition-transform duration-500 group-hover:translate-x-3"
                >
                  {s.title}
                </h3>
                <p className="col-span-12 md:col-span-5 md:col-start-6 text-dm-dark/55 leading-relaxed">
                  {s.desc}
                </p>
                <span className="hidden md:flex col-span-2 justify-end">
                  <span className="grid place-items-center h-11 w-11 rounded-full border transition-colors duration-300 group-hover:bg-dm-maroon group-hover:border-dm-maroon"
                    style={{ borderColor: "rgba(10,10,10,0.2)" }}>
                    <ArrowUpRight size={18} className="text-dm-dark transition-colors duration-300 group-hover:text-white" />
                  </span>
                </span>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}
